import { useState, useEffect } from 'react';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 1400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ['hero', 'o-nas', 'ponudba', 'dozivetje', 'delovni-cas', 'galerija', 'kontakt'];
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  const scrollTo = (id: string) => {
    setIsMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'O nas', id: 'o-nas' },
    { label: 'Ponudba', id: 'ponudba' },
    { label: 'Doživetje', id: 'dozivetje' },
    { label: 'Delovni čas', id: 'delovni-cas' },
    { label: 'Galerija', id: 'galerija' },
    { label: 'Kontakt', id: 'kontakt' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      } ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); scrollTo('hero'); }}
            className="flex items-center gap-2.5 md:gap-3.5 cursor-pointer group"
          >
            <img
              src="https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/d1b6ec71613fd44f076bb20c8764731b.png"
              alt="Guma Caffe Lounge Bar Gorišnica"
              title="Guma Caffe Lounge Bar — kava in prijeten ambient v Zamušanih"
              className="h-8 md:h-11 w-auto object-contain flex-shrink-0"
            />
            <span className="flex flex-col leading-tight border-l border-foreground-50/20 pl-2.5 md:pl-3.5">
              <span className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-accent-400 whitespace-nowrap">
                <i className="ri-trophy-line"></i>
                Zmagovalec
              </span>
              <span className="text-[9px] md:text-[11px] text-foreground-50/80 whitespace-nowrap">
                Naj kava Ptujinfo 2024
              </span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = activeSection === link.id;
              return (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className={`relative whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 cursor-pointer ${
                    isScrolled
                      ? isActive
                        ? 'text-foreground-50 bg-background-100'
                        : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-100'
                      : isActive
                      ? 'text-foreground-50 bg-foreground-50/15'
                      : 'text-foreground-50/90 hover:text-foreground-50 hover:bg-foreground-50/10'
                  }`}
                >
                  {link.label}
                  <span
                    className={`absolute left-1/2 -translate-x-1/2 bottom-0.5 h-[2px] rounded-full bg-primary-400 transition-all duration-300 ${
                      isActive ? 'w-4 opacity-100' : 'w-0 opacity-0'
                    }`}
                  ></span>
                </button>
              );
            })}
            <a
              href="tel:051240383"
              className={`whitespace-nowrap ml-3 px-5 py-2.5 rounded-md text-sm font-semibold transition-all duration-300 cursor-pointer bg-primary-500 text-foreground-50 hover:bg-primary-600`}
            >
              <i className="ri-phone-line mr-1.5"></i>
              Pokliči
            </a>
            <a
              href="https://www.facebook.com/GumaBarZamusani"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook — Guma Caffe Lounge Bar"
              className={`whitespace-nowrap ml-2 w-10 h-10 flex items-center justify-center rounded-md text-lg transition-all duration-300 cursor-pointer ${
                isScrolled
                  ? 'text-secondary-500 hover:bg-background-100'
                  : 'text-foreground-50 hover:bg-foreground-50/10'
              }`}
            >
              <i className="ri-facebook-circle-fill"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled ? 'text-foreground-200 hover:bg-background-100' : 'text-foreground-50 hover:bg-foreground-50/10'
            }`}
            aria-label="Meni"
          >
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="w-full text-left px-4 py-3 rounded-md text-foreground-200 hover:text-foreground-50 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
            <a
              href="tel:051240383"
              className="mt-2 w-full text-center px-5 py-3 rounded-md bg-primary-500 text-foreground-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-1.5"></i>
              Pokliči zdaj
            </a>
            <a
              href="https://www.facebook.com/GumaBarZamusani"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-center px-5 py-3 rounded-md border border-background-300 text-foreground-200 hover:text-secondary-500 hover:border-secondary-400 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-facebook-circle-fill mr-1.5"></i>
              Facebook
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}